import React, { useState } from "react";
import {
  Card,
  CardMedia,
  Box,
  Typography,
  Chip,
  IconButton,
  Fade,
  Modal,
  Backdrop,
  Skeleton,
  Tooltip,
  CircularProgress,
  useTheme,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import {
  DeleteOutlined,
  ZoomInOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  BrushOutlined,
  AutoAwesomeOutlined,
} from "@mui/icons-material";
import CommonDialog from "../../../common/CommonDialog";
import ConfirmAlert from "../../../common/ConfirmAlert";

const InfluencerCard = ({ influencer, onDelete, onRename, onEditInStudio, lang }) => {
  const theme = useTheme();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isActive = influencer?.status === "active";
  const isError = influencer?.status === "error";
  const hasPhoto = !!influencer?.photo_url;
  const canOpenLightbox = isActive && hasPhoto;
  const canDownload = isActive && hasPhoto && influencer?.id;

  const handleImageClick = () => {
    if (canOpenLightbox) setLightboxOpen(true);
  };

  const handleDownload = async (e) => {
    e?.stopPropagation?.();
    if (!canDownload || downloading) return;

    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN + "_user",
    );
    const apiBase = (
      process.env.REACT_APP_BASE_URL || "http://localhost:8001"
    ).replace(/\/+$/, "");
    const url = `${apiBase}/api/inf/download/${influencer.id}`;

    setDownloading(true);
    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const ct = (res.headers.get("Content-Type") || "").toLowerCase();
      if (ct.includes("application/json")) {
        let msg = lang?.downloadFailed || "Download failed";
        try {
          const j = await res.json();
          msg = j.msg || j.message || (Array.isArray(j.message) ? j.message.join(", ") : msg);
        } catch {
          /* */
        }
        throw new Error(msg);
      }
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      let filename = `${String(influencer.name || "character").replace(/[^\w\-]+/g, "_")}_${influencer.id}.png`;
      const cd = res.headers.get("Content-Disposition");
      if (cd) {
        const m = cd.match(/filename="([^"]+)"/);
        if (m?.[1]) filename = m[1];
      }
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert(
        err?.message ||
          lang?.downloadFailed ||
          "Download failed. Try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const openRename = (e) => {
    e?.stopPropagation?.();
    if (!onRename || renaming) return;
    setRenameValue(influencer.name || "");
    setRenameOpen(true);
  };

  const submitRename = async () => {
    const trimmed = String(renameValue || "").trim();
    if (!trimmed || trimmed === influencer.name) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    try {
      await onRename(influencer.id, trimmed);
      setRenameOpen(false);
    } finally {
      setRenaming(false);
    }
  };

  const openDelete = (e) => {
    e?.stopPropagation?.();
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!onDelete || deleting) return;
    setDeleting(true);
    try {
      await onDelete(influencer.id);
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const openInStudio = (mode = "edit") => {
    if (!onEditInStudio || !hasPhoto) return;
    setLightboxOpen(false);
    onEditInStudio(influencer, { mode });
  };

  return (
    <>
      <Card
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          transition: "transform 0.2s",
          height: 320,
          "&:hover": {
            transform: "translateY(-4px)",
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {hasPhoto ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "relative",
              cursor: canOpenLightbox ? "zoom-in" : "default",
              ...(canOpenLightbox && {
                "&:hover .zoom-hint": { opacity: 1 },
              }),
            }}
            onClick={handleImageClick}
          >
            <CardMedia
              component="img"
              height="100%"
              image={influencer.photo_url}
              alt={influencer.name}
              loading="lazy"
              decoding="async"
              sx={{
                objectFit: "cover",
                height: "100%",
                width: "100%",
                objectPosition: influencer.photo_focus ?? "center 15%",
              }}
            />

            {canOpenLightbox && (
              <Box
                className="zoom-hint"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.35)",
                  opacity: 0,
                  transition: "opacity 0.25s ease",
                }}
              >
                <ZoomInOutlined sx={{ color: "white", fontSize: 48 }} />
              </Box>
            )}
          </Box>
        ) : (
          <Skeleton
            sx={{ bgcolor: "grey.900" }}
            variant="rectangular"
            width="100%"
            height="100%"
          />
        )}

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
            p: 2,
            pointerEvents: "none",
          }}
        >
          <Typography variant="h6" color="white" fontWeight={700} noWrap>
            {influencer.name}
          </Typography>
          <Typography
            variant="body2"
            color="white"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              opacity: 0.9,
            }}
          >
            {influencer.description}
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            display: "flex",
            gap: 0.5,
          }}
        >
          {canDownload && (
            <Tooltip title={lang?.characterDownload || "Download image"}>
              <span>
                <IconButton
                  onClick={handleDownload}
                  disabled={downloading}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "white",
                    "&:hover": { bgcolor: "primary.main", color: "white" },
                  }}
                >
                  {downloading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadOutlined fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onRename && (
            <Tooltip title={lang?.renameImage || "Rename"}>
              <span>
                <IconButton
                  onClick={openRename}
                  disabled={renaming}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "white",
                    "&:hover": { bgcolor: "primary.main", color: "white" },
                  }}
                >
                  {renaming ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <EditOutlined fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            onClick={openDelete}
            size="small"
            sx={{
              bgcolor: "rgba(0,0,0,0.45)",
              color: "white",
              "&:hover": { bgcolor: "error.main", color: "white" },
            }}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
            maxWidth: "calc(100% - 48px)",
          }}
        >
          {influencer.status && (
            <Chip
              label={
                isActive
                  ? lang?.active || "Active"
                  : isError
                    ? lang?.failed || "Failed"
                    : lang?.processing || "Processing"
              }
              size="small"
              color={isActive ? "success" : isError ? "error" : "warning"}
            />
          )}
          {isError && influencer.error_message && (
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                bgcolor: "rgba(211,47,47,0.85)",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                maxWidth: "100%",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {influencer.error_message}
            </Typography>
          )}
        </Box>
      </Card>

      <CommonDialog
        open={renameOpen}
        onClose={() => !renaming && setRenameOpen(false)}
        title={lang?.renameImage || "Rename image"}
        subtitle={lang?.renameImageHint || "Give this image a clear name."}
        icon={EditOutlined}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            label={lang?.name || "Name"}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitRename();
              }
            }}
            inputProps={{ maxLength: 120 }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              disabled={renaming}
              onClick={() => setRenameOpen(false)}
              sx={{ fontWeight: 600 }}
            >
              {lang?.cancel || "Cancel"}
            </Button>
            <Button
              variant="contained"
              disabled={renaming || !String(renameValue || "").trim()}
              onClick={submitRename}
              sx={{ fontWeight: 700, minWidth: 96 }}
            >
              {renaming ? <CircularProgress size={20} color="inherit" /> : lang?.save || "Save"}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>

      <ConfirmAlert
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={lang?.deleteImageTitle || "Delete this image?"}
        message={
          lang?.deleteImageMessage ||
          `“${influencer.name || "This image"}” will be permanently removed.`
        }
        confirmLabel={lang?.delete || "Delete"}
        cancelLabel={lang?.cancel || "Cancel"}
        confirmColor="error"
        loading={deleting}
        icon={DeleteOutlined}
      />

      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 300,
            sx: { bgcolor: "rgba(0,0,0,0.92)" },
          },
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
      >
        <Fade in={lightboxOpen} timeout={300}>
          <Box
            sx={{
              position: "relative",
              outline: "none",
              maxWidth: "min(920px, 94vw)",
              maxHeight: "92vh",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: 24,
              bgcolor: "#111",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10,
                display: "flex",
                gap: 0.75,
                bgcolor: "rgba(0,0,0,0.45)",
                borderRadius: 999,
                p: 0.35,
              }}
            >
              {onEditInStudio && canOpenLightbox && (
                <Tooltip title={lang?.imageStudioEdit || "Edit in Image Studio"}>
                  <IconButton
                    onClick={() => openInStudio("edit")}
                    sx={{ color: "white", "&:hover": { bgcolor: "primary.main" } }}
                  >
                    <BrushOutlined />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={lang?.characterDownload || "Download image"}>
                <span>
                  <IconButton
                    onClick={handleDownload}
                    disabled={downloading}
                    sx={{ color: "white", "&:hover": { bgcolor: "primary.main" } }}
                  >
                    {downloading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      <DownloadOutlined />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={() => setLightboxOpen(false)}
                sx={{ color: "white", "&:hover": { bgcolor: "error.main" } }}
              >
                <CloseOutlined />
              </IconButton>
            </Box>

            <Box
              component="img"
              src={influencer.photo_url}
              alt={influencer.name}
              sx={{
                display: "block",
                maxWidth: "min(920px, 94vw)",
                maxHeight: "68vh",
                objectFit: "contain",
                mx: "auto",
              }}
            />

            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderTop: "1px solid rgba(255,255,255,0.08)",
                background: "linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.65))",
              }}
            >
              <Typography variant="subtitle1" color="white" fontWeight={700}>
                {influencer.name}
              </Typography>
              {(influencer.description || influencer.prompt) && (
                <Typography
                  variant="caption"
                  color="white"
                  sx={{
                    opacity: 0.8,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 1.5,
                  }}
                >
                  {influencer.prompt || influencer.description}
                </Typography>
              )}
              {onEditInStudio && canOpenLightbox ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<BrushOutlined />}
                    onClick={() => openInStudio("edit")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: "#f5f5f5",
                      color: "#111",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    {lang?.imageStudioEditInStudio || "Edit in Image Studio"}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AutoAwesomeOutlined />}
                    onClick={() => openInStudio("reference")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.28)",
                      "&:hover": {
                        borderColor: "#fff",
                        bgcolor: "rgba(255,255,255,0.06)",
                      },
                    }}
                  >
                    {lang?.imageStudioUseInStudio || "Use in Image Studio"}
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default InfluencerCard;
